import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Medications } from '../../../../../packages/ui/templates/sign-off/components/medications';
import { Icons } from '../../../../../packages/icons';
import { colors } from '../../../../../packages/colors';
import { DomainTypesEnum } from '../../../../../constants/CommonConstants';
import { profileActionCreators } from '../../../../../redux/modules/profile';
import { priorityActionCreators } from '../../../../../redux/modules/priority';
import AddNewReportMedications from '../../../../provider-v2/member-detail/add-new-report/AddNewReportMedications';
import { getAssignedPrioritiesV2 } from '../../../../../services/priority/priority.service';
import { getProfile } from '../../../../../redux/modules/profile/selectors';
import { getAuth } from '../../../../../redux/modules/auth/selectors';
import { getDomainGroup } from '../../../../../services/member/member.service';
import { PriorityDetailDrawer } from '../../../priorities/PriorityDetailDrawer';
import AddFactorDrawer, { DrawerType } from '../../../../provider-v2/member-detail/add-factor-drawer';
import { FactorType } from '../../../../provider-v2/member-detail/add-factor-drawer/FactorItem';
import AddExternalRxDrawer from '../../../../provider-v2/member-detail/medications/AddExternalRxDrawer';
import { MEDICATION_MODE } from '../../../../provider-v2/member-detail/add-new-report/AddNewReport.constants';
import {
  fetchNavItemStatus,
  initiateMdToolboxAutomation,
} from '../../../../../services/datalab/datalab.service';
import { showSnackbar } from '../../../../../redux/modules/snackbar';
import { mapToLegacy } from '../../../../../utils/CommonUtils';
import { DOMAIN_ELEMENT_VARIENT } from '../../../../provider-v2/member-detail/member-detail.constents';
import {
  flattenExternalRxRecords,
  isExternalRxRecord,
  sortMedicationsWithExternalLast,
} from '../../../../../utils/medicationUtils';

export const MedicationSection = ({ appointment, isSession }) => {
  const dispatch = useDispatch();

  const { meta } = useSelector(getAuth);
  const { lookupData, domainTypes, profile, demographicDetails } = useSelector(getProfile);

  const [openMedicationDrawer, setOpenMedicationDrawer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDomainElement, setSelectedDomainElement] = useState(null);
  const [domainData, setDomainData] = useState({});
  const [reset, setReset] = useState(false);
  const [domainOptions, setDomainOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [shouldStartAutomation, setShouldStartAutomation] = useState(false);
  const [isAutomationLoading, setIsAutomationLoading] = useState(false);
  const [automationSessionId, setAutomationSessionId] = useState(null);
  const [isSavingMedication, setIsSavingMedication] = useState(false);
  const [showMedicationSelection, setShowMedicationSelection] = useState(false);
  const [showExternalRx, setShowExternalRx] = useState(false);
  const [existingExternalRx, setExistingExternalRx] = useState(null);
  const [medicationMode, setMedicationMode] = useState(MEDICATION_MODE.ADD);
  const [existingMedicationForDrawer, setExistingMedicationForDrawer] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  const otherDomainElementId = useMemo(() => {
    for (const group of domainOptions) {
      const otherItem = group.items.find(item => item.label.toLowerCase() === 'other');
      if (otherItem) {
        return otherItem.value;
      }
    }
    return null;
  }, [domainOptions]);

  const fetchMedications = memberId => {
    // Don't fetch data while medication is being saved to prevent UI refresh
    if (isSavingMedication) {
      return;
    }

    setIsLoading(true);
    getAssignedPrioritiesV2({
      patientId: memberId,
      dataDomainType: 'medications',
      pageNumber: 0,
      pageSize: 100,
      searchQuery: '',
    })
      .then(({ data }) => {
        setDomainData(data);
        setIsLoading(false);
      })
      .catch(e => {
        console.error(e);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    dispatch(priorityActionCreators.fetchPriorityDomainTypes());
    dispatch(profileActionCreators.fetchDemographicMetadata());
    if (appointment?.participantId) {
      dispatch(profileActionCreators.fetchDemographicDetails(appointment.participantId));
    }
  }, [appointment?.participantId]);

  useEffect(() => {
    if (appointment) {
      fetchMedications(appointment.participantId);
    }
  }, [appointment, reset]);

  const handleAddClick = () => {
    setSelectedDomainElement(null);
    setShowMedicationSelection(true);
    // Reset automation state when opening drawer
    setShouldStartAutomation(false);
    setIsAutomationLoading(false);
    setAutomationSessionId(null);
  };

  const handleUpdateClick = medication => {
    const isExternalRx = isExternalRxRecord(medication);
    if (isExternalRx) {
      setExistingExternalRx(medication);
      setShowExternalRx(true);
    } else {
      // Transform to legacy format for PriorityDetailDrawer
      setSelectedDomainElement(mapToLegacy(medication, lookupData, DOMAIN_ELEMENT_VARIENT.MEDICATION));
      setMedicationMode(MEDICATION_MODE.UPDATE);
      setExistingMedicationForDrawer(medication);
      setSelectedType(FactorType.MEDICATIONS);
      setOpenMedicationDrawer(true);
    }
  };

  const onCloseDrawer = (shouldClose = true) => {
    if (shouldClose) {
      setOpenMedicationDrawer(false);
      setSelectedDomainElement(null);
    }
    setShouldStartAutomation(false);
    setIsAutomationLoading(false);
    setAutomationSessionId(null);
    setIsSavingMedication(false);
    setReset(!reset);
  };

  const startMdToolboxAutomation = async (patientId, associatedTagId, confidantProviderId) => {
    setIsAutomationLoading(true);
    try {
      const response = await initiateMdToolboxAutomation(
        {
          patientId,
          associatedTagId,
          providerId: confidantProviderId,
        },
        meta.userId,
      );
      if (!response.data?.success) {
        // Extract specific error message from backend response
        const errorMessage =
          response.data?.error?.message ||
          response.data?.message ||
          'Failed to submit the medication data. Please try again.';
        throw new Error(errorMessage);
      }
      // Extract sessionId from the response
      const sessionId = response.data?.sessionId;
      if (!sessionId) {
        console.warn('No sessionId found in automation response');
      }

      setIsAutomationLoading(false);
      return { ...response, sessionId, isQueued: response.data?.isQueued };
    } catch (error) {
      setIsAutomationLoading(false);
      const errorMessage =
        error.data?.error?.message || 'Failed to submit the medication data. Please try again.';
      throw new Error(errorMessage);
    }
  };

  useEffect(() => {
    const domain = domainTypes.find(item => item.typeName === DomainTypesEnum.MEDICATIONS);
    if (domain) {
      setOptionsLoading(true);
      fetchDomainForm(domain.typeId);
    }
  }, [domainTypes]);

  const fetchDomainForm = typeId => {
    getDomainGroup({ domainTypeId: typeId })
      .then(({ data }) => {
        const opts = data.map(item => ({
          label: item.name,
          groupId: item.Id,
          items: item.relatedElements.map(element => ({ label: element.name, value: element.Id })),
          relatedElements: item.relatedElements,
        }));
        setDomainOptions(opts);
        setOptionsLoading(false);
      })
      .catch(() => {
        setDomainOptions([]);
        setOptionsLoading(false);
      });
  };

  const onSubmitNewReport = (payload, handleQueued) => {
    setIsSavingMedication(true);

    const data = {
      patientId: appointment.participantId,
      tagItems: payload.medications.map(item => ({
        assignedBy: profile.fullName || meta.nickName,
        domainElementId: item.item.value,
        metaData: {
          notes: item?.additionalNotes,
          selectedImportance: item?.status,
          rxDrugInfo: {
            sig: item?.sig,
            externalMedicationId: item?.externalMedicationId,
            externalMedicationName: item?.externalMedicationName,
            refills: item?.refills,
            dispenseAmount: item?.dispenseAmount ? Number(item.dispenseAmount) : 0,
            associatedDiagnoses: item?.associatedDiagnoses,
            allowSubstitutions: item?.allowSubstitutions,
            pharmacyNotes: item?.pharmacyNotes,
            additionalNotes: item?.additionalNotes,
            quantity: item.quantity ? Number(item.quantity) : 0,
            dose: item.dose ?? '',
            frequency: item?.frequency?.value ?? item?.frequency,
            duration: item?.duration ? Number(item.duration) : 0,
            confidantProviderId: item?.confidantProvider,
            confidantProviderName: item?.providerName,
            prescribingProvider: item?.provider === 'Confidant Provider' ? 'Internal' : 'External',
            providerName: item?.providerName,
            symptomsBeingTreated: item?.symptoms,
            pharmacyId: payload.pharmacy?.id,
            pharmacyName: payload.pharmacy?.name,
            pharmacyCode: payload.pharmacy?.externalId,
            pharmacyZipCode: payload.pharmacy?.zipCode,
          },
        },
      })),
    };

    dispatch(
      profileActionCreators.createNewAssociatedTag({
        data,
        callback: async (errorMsg, response) => {
          if (errorMsg) {
            setIsSavingMedication(false);
            dispatch(
              showSnackbar({
                snackType: 'error',
                snackMessage: errorMsg || 'Failed to create medication. Please try again.',
              }),
            );
            onCloseDrawer();
            return;
          }

          try {
            // Fetch the records again to get the newly created association
            const { data: updatedRecords } = await getAssignedPrioritiesV2({
              patientId: appointment.participantId,
              dataDomainType: 'medications',
              pageNumber: 0,
              pageSize: 20,
              searchQuery: '',
            });

            // Find the newly created association by matching domainElementId
            const targetDomainElementId = payload.medications[0].item.value;
            const newlyCreatedAssociation = updatedRecords.records.find(
              record => record.domainElementId === targetDomainElementId,
            );

            if (!newlyCreatedAssociation) {
              throw new Error('Could not find the newly created medication association');
            }

            setIsSavingMedication(false);

            // Use the association ID from the fetched record
            const associatedTagId = newlyCreatedAssociation.id;
            const confidantProviderId = payload.medications[0]?.confidantProvider;

            if (confidantProviderId) {
              const status = await fetchNavItemStatus({ providerId: confidantProviderId });
              const statusData = status?.data?.data;
              if (!statusData || !statusData['MDToolboxAccess']) {
                // Silently skip automation because of missing mdtoolbox credentials
                onCloseDrawer();
                return;
              }

              const { sessionId, isQueued } = await startMdToolboxAutomation(
                appointment.participantId,
                associatedTagId,
                confidantProviderId,
              );

              if (sessionId) {
                // Store sessionId and trigger automation in child component
                setAutomationSessionId(sessionId);
                setShouldStartAutomation(true);
                // Don't close drawer - let automation handle the flow
                if (isQueued && handleQueued) {
                  handleQueued();
                }
              } else {
                console.warn('No Session id received, cannot submit medication');
                onCloseDrawer();
              }
            } else {
              // No confidantProviderId, just close the drawer normally
              onCloseDrawer();
            }
          } catch (error) {
            dispatch(
              showSnackbar({
                snackType: 'error',
                snackMessage: error.message || 'Failed to submit the medication data. Please try again.',
              }),
            );
            // Reset automation states but keep drawer open
            onCloseDrawer(false);
          }
        },
      }),
    );
  };

  // Flatten external RX records directly from raw API data (same as medications/index.jsx)
  const flattenedRecords = useMemo(() => {
    return flattenExternalRxRecords(domainData?.records);
  }, [domainData]);

  // Sort and map for template component
  const mappedMedications =
    sortMedicationsWithExternalLast(flattenedRecords).map(record => ({
      ...record,
      // Map fields expected by template
      title: record.taxonomyName,
      sig: record.tagMetaData?.rxDrugInfo?.sig || '',
      updatedAt: record.assignedAt,
      drug: record.tagMetaData?.rxDrugInfo?.externalMedicationName || '',
      dose: record.tagMetaData?.rxDrugInfo?.dose || '',
      frequency: record.tagMetaData?.rxDrugInfo?.frequency || '',
      duration: record.tagMetaData?.rxDrugInfo?.duration || '',
      quantity: record.tagMetaData?.rxDrugInfo?.quantity || '',
      refills: record.tagMetaData?.rxDrugInfo?.refills || 0,
      dispenseAmount: record.tagMetaData?.rxDrugInfo?.dispenseAmount || '',
      allowSubstitutions: record.tagMetaData?.rxDrugInfo?.allowSubstitutions || false,
      pharmacyName: record.tagMetaData?.rxDrugInfo?.pharmacyName || '',
      pharmacyZipCode: record.tagMetaData?.rxDrugInfo?.pharmacyZipCode || '',
      status: record.currentImportanceLevel || 'Active',
    })) || [];

  const activeMedications = mappedMedications.filter(
    medication =>
      medication.status.trim().toLowerCase() === 'active' ||
      medication.status.trim().toLowerCase() === 'active_external_rx',
  );

  const onAutomationError = () => {
    // Reset loading states but keep drawer open
    setIsSavingMedication(false);
    setIsAutomationLoading(false);
    setShouldStartAutomation(false);
    setAutomationSessionId(null);
  };

  return (
    <Box display="flex" flexDirection="column" gap="40px">
      {!isSession && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 28,
              lineHeight: '36px',
              color: colors.neutral900,
            }}
          >
            Review medications
          </Typography>
        </Box>
      )}
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
        </Box>
      ) : (
        <Medications
          patientState={appointment?.patientState || ''}
          onAddClick={handleAddClick}
          medications={activeMedications}
          onUpdate={handleUpdateClick}
          uuid={demographicDetails?.uuid}
          pdmpSummary={demographicDetails?.pdmpSummary}
          pdmpLastUpdated={demographicDetails?.pdmpLastUpdated}
        />
      )}
      <AddFactorDrawer
        open={showMedicationSelection}
        onClose={() => setShowMedicationSelection(false)}
        title="Add Medication"
        variant={DrawerType.MEDICATION}
        onChangeSelect={selected => {
          setShowMedicationSelection(false);
          if (selected === FactorType.SEND_RX) {
            setSelectedType(FactorType.MEDICATIONS);
            setMedicationMode(MEDICATION_MODE.ADD);
            setOpenMedicationDrawer(true);
          } else if (selected === FactorType.EXTERNAL_RX) {
            setShowExternalRx(true);
          }
        }}
      />
      <AddNewReportMedications
        isOpen={selectedType === FactorType.MEDICATIONS && openMedicationDrawer}
        onClose={forceRefresh => {
          if (!isSavingMedication && !shouldStartAutomation && !isAutomationLoading) {
            setSelectedType(null);
            setMedicationMode(MEDICATION_MODE.ADD);
            setExistingMedicationForDrawer(null);
            onCloseDrawer();
          }
          if (forceRefresh) {
            fetchMedications(appointment?.participantId);
          }
        }}
        options={domainOptions}
        lookupData={lookupData}
        onSubmit={onSubmitNewReport}
        optionsLoading={optionsLoading}
        shouldStartAutomation={shouldStartAutomation}
        setShouldStartAutomation={setShouldStartAutomation}
        isAutomationLoading={isAutomationLoading}
        automationSessionId={automationSessionId}
        onAutomationSuccess={() => {
          setAutomationSessionId(null);
          setShouldStartAutomation(false);
          setIsAutomationLoading(false);
        }}
        onAutomationError={onAutomationError}
        isSaving={isSavingMedication}
        mode={medicationMode}
        existingMedication={existingMedicationForDrawer}
      />
      <AddExternalRxDrawer
        open={showExternalRx}
        onClose={() => {
          setShowExternalRx(false);
          setExistingExternalRx(null);
          setReset(!reset);
        }}
        memberId={appointment?.participantId}
        otherDomainElementId={otherDomainElementId}
        existingRecord={existingExternalRx}
      />
      {selectedDomainElement?.taxonomyName && openMedicationDrawer && (
        <PriorityDetailDrawer
          memberId={appointment?.participantId}
          selectedItem={selectedDomainElement}
          type="medications"
          onClose={() => {
            setSelectedDomainElement(null);
            setOpenMedicationDrawer(false);
          }}
          open={selectedDomainElement?.taxonomyName && openMedicationDrawer}
          onElementsUpdated={() => setReset(!reset)}
        />
      )}
    </Box>
  );
};
